import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DiagnosticField } from "../echoflicks/journey/DiagnosticFields";
import { duration, elapsed, label, timestamp } from "./format";
import type { MonitorStage, QueueRunDetail, TimingBaseline } from "./types";
import { useQueueResource } from "./useQueueResource";
import { useNow } from "../whatsapp/inbox/useInbox";
import { DiagnosticEvidence } from "./DiagnosticEvidence";
import { stageLabel } from "./stageLabel";
import { PipelineArtifacts } from "./PipelineArtifacts";

function ReferenceList({
  title,
  references,
}: {
  title: string;
  references: string[];
}) {
  return (
    <section>
      <h4 className="mb-1 font-medium">{title}</h4>
      {references.length ? (
        <ul className="space-y-1">
          {references.map((reference) => (
            <li
              key={reference}
              className="break-all rounded bg-muted p-2 font-mono text-xs"
            >
              {reference}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground">No evidence reference recorded.</p>
      )}
    </section>
  );
}

export function PipelineNodeInspector({
  stage,
  detail,
  pinned,
  onUnpin,
}: {
  stage: MonitorStage;
  detail: QueueRunDetail;
  pinned: boolean;
  onUnpin: () => void;
}) {
  const [attemptCursor, setAttemptCursor] = useState<string | null>(null);
  const now = useNow(1000);
  const path =
    pinned && detail.capabilities.attempts
      ? `/pipeline/runs/${encodeURIComponent(detail.run.workload_id)}/stages/${encodeURIComponent(stage.stage_instance_id)}?epoch=${detail.run.execution_epoch}${attemptCursor ? `&cursor=${encodeURIComponent(attemptCursor)}` : ""}`
      : null;
  const attempts = useQueueResource<QueueRunDetail>(path, "detail");
  const baseline = stage.baseline_ref
    ? detail.baselines[stage.baseline_ref]
    : undefined;
  return (
    <aside
      aria-label="Step inspector"
      className="space-y-4 rounded-lg border p-4 text-sm"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold">
            {stageLabel(
              stage,
              detail.definition.stages.find(
                (item) => item.id === stage.stage_id,
              )?.label,
            )}
          </h3>
          <p className="text-xs text-muted-foreground">
            {pinned ? "Pinned step" : "Preview · click a step to pin"}
          </p>
        </div>
        {pinned && (
          <Button size="sm" variant="ghost" onClick={onUnpin}>
            Unpin
          </Button>
        )}
      </div>
      <dl className="grid grid-cols-2 gap-3">
        <DiagnosticField label="State">{label(stage.state)}</DiagnosticField>
        {stage.provider_route && (
          <DiagnosticField label="Provider">
            {label(stage.provider_route)}
          </DiagnosticField>
        )}
        {stage.model_route && (
          <DiagnosticField label="Model family">
            {label(stage.model_route)}
          </DiagnosticField>
        )}
        <DiagnosticField label="Evidence">
          {label(stage.evidence_quality)}
        </DiagnosticField>
        <DiagnosticField label="Started">
          {timestamp(stage.started_at)}
        </DiagnosticField>
        <DiagnosticField label="Elapsed">
          {stage.duration_ms != null
            ? duration(stage.duration_ms)
            : elapsed(
                stage.started_at,
                stage.finished_at ?? new Date(now).toISOString(),
              )}
        </DiagnosticField>
        <DiagnosticField label="Wait reason">
          {stage.wait_reason ? label(stage.wait_reason) : "None recorded"}
        </DiagnosticField>
        <DiagnosticField label="Last progress">
          {timestamp(stage.progress_at)}
        </DiagnosticField>
        {stage.progress && (
          <DiagnosticField label="Progress">
            {stage.progress.completed} / {stage.progress.total}{" "}
            {stage.progress.unit}
          </DiagnosticField>
        )}
        {stage.outcome_reason && (
          <DiagnosticField label="Outcome">
            {label(stage.outcome_reason)}
          </DiagnosticField>
        )}
      </dl>
      <BaselineStats baseline={baseline} />
      <DiagnosticEvidence stage={stage} environment={detail.run.environment} />
      <ReferenceList title="Inputs" references={stage.input_refs} />
      <ReferenceList title="Outputs" references={stage.output_refs} />
      {pinned && <PipelineArtifacts stage={stage} detail={detail} />}
      <ReferenceList
        title="Execution references"
        references={stage.execution_refs}
      />
      {pinned && (
        <section>
          <h4 className="mb-2 font-medium">Attempts</h4>
          {attempts.error && (
            <p role="status">
              Attempt evidence is unavailable.{" "}
              <Button variant="link" onClick={attempts.reload}>
                Retry
              </Button>
            </p>
          )}
          {attempts.data?.items.map((attempt) => (
            <div key={attempt.attempt_id} className="mb-2 rounded border p-2">
              <p>
                {attempt.attempt_id} · {label(attempt.state)}
              </p>
              <p className="text-xs text-muted-foreground">
                {timestamp(attempt.started_at)} ·{" "}
                {duration(attempt.duration_ms)}
              </p>
              <DiagnosticEvidence
                title="Attempt diagnostic"
                stage={attempt}
                environment={detail.run.environment}
              />
            </div>
          ))}
          {!attempts.loading &&
            !attempts.error &&
            !attempts.data?.items.length && (
              <p className="text-muted-foreground">No attempts recorded.</p>
            )}
          {attempts.data?.next_cursor && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAttemptCursor(attempts.data!.next_cursor)}
            >
              Next attempts
            </Button>
          )}
          {attemptCursor && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAttemptCursor(null)}
            >
              First attempts
            </Button>
          )}
        </section>
      )}
    </aside>
  );
}

function BaselineStats({ baseline }: { baseline?: TimingBaseline }) {
  if (!baseline || baseline.quality !== "ready")
    return (
      <section>
        <h4 className="font-medium">Typical duration</h4>
        <p className="text-muted-foreground">
          {baseline?.quality === "insufficient_samples"
            ? `Insufficient samples (${baseline.sample_count})`
            : "Baseline unavailable"}
        </p>
      </section>
    );
  const percentile = (value: TimingBaseline["p90"]) =>
    value
      ? `${duration(value.lower_ms)}–${duration(value.upper_ms)} (estimated)`
      : "Unavailable";
  return (
    <section>
      <h4 className="mb-2 font-medium">Typical duration</h4>
      <dl className="grid grid-cols-2 gap-2">
        <DiagnosticField label="Mean">
          {duration(baseline.mean_ms)}
        </DiagnosticField>
        <DiagnosticField label="p50">
          {percentile(baseline.p50)}
        </DiagnosticField>
        <DiagnosticField label="p90">
          {percentile(baseline.p90)}
        </DiagnosticField>
        <DiagnosticField label="Population">
          {baseline.sample_count} samples · {baseline.window_days} days
        </DiagnosticField>
      </dl>
      <p className="mt-2 text-xs text-muted-foreground">
        {label(baseline.cohort.population)} · {baseline.cohort.route}
        {baseline.cohort.model_route
          ? ` / ${baseline.cohort.model_route}`
          : ""}{" "}
        · Updated {baseline.as_of}
      </p>
      {baseline.stale && (
        <p role="status" className="text-xs text-muted-foreground">
          This baseline is stale. Treat it as a historical reference.
        </p>
      )}
    </section>
  );
}
